/**
 * Owner: Masa Bayu
 * File: AdminOverviewScreen.tsx
 * Date:Time: 2026-10-23 09:50
 * Description: §467-D — seksyen Ringkasan: KPI stor hub, hasil per mata wang
 *   (tiada jumlah rentas kod), siri pesanan berbayar mengikut bulan, senarai
 *   terbitan tanpa pemilik, dan order terkini. Semua nombor ialah kiraan yang
 *   DIKIRA DI HUB — klien hanya memaparkan.
 */

import { useState } from 'react';
import type { ReactElement } from 'react';
import type { MarketplaceLanguage } from '../home/marketplace/marketplace-locale';
import type { AdminLabels } from './admin-labels';
import type { AdminDashboardView } from './admin-dashboard';
import {
  dateTimeText,
  moneyText,
  monthlySeries,
  overviewKpis,
  seriesMax,
  seriesRatio,
  stateLabel,
  stateTone,
} from './admin-view';
import { AdminMoneyList } from './AdminMoneyList';
import { Icon } from './AdminIcons';

function MonthlyBars({
  view,
  la,
  language,
}: {
  readonly view: AdminDashboardView;
  readonly la: AdminLabels;
  readonly language: MarketplaceLanguage;
}): ReactElement {
  const points = monthlySeries(view, language, la.noData);
  const max = seriesMax(points);
  if (points.length === 0) {
    return <p className="qxa__empty">{la.monthlyEmpty}</p>;
  }
  return (
    <div className="qxa__bars" role="img" aria-label={la.monthlyAria}>
      {points.map((point) => {
        const ratio = seriesRatio(point.orders, max);
        return (
          <div key={point.month} className="qxa__bar-col" title={point.rows.join('  |  ')}>
            <span className="qxa__bar-count">{String(point.orders)}</span>
            <span
              className="qxa__bar"
              style={{ height: `${Math.round(ratio * 96) + 4}px` }}
              aria-hidden="true"
            />
            <span className="qxa__bar-label">{point.label}</span>
          </div>
        );
      })}
    </div>
  );
}

export interface AdminOverviewScreenProps {
  readonly view: AdminDashboardView;
  readonly la: AdminLabels;
  readonly language: MarketplaceLanguage;
  readonly onNavigate: (path: string) => void;
}

export function AdminOverviewScreen({
  view,
  la,
  language,
  onNavigate,
}: AdminOverviewScreenProps): ReactElement {
  const [showAllRecent, setShowAllRecent] = useState(false);
  const kpis = overviewKpis(view, la);
  const unowned = view.catalog.unownedPublications;
  const recent = showAllRecent ? view.recentOrders : view.recentOrders.slice(0, 8);

  return (
    <div className="qxa__stack">
      <section className="qxa__kpis" aria-label={la.overviewTitle}>
        {kpis.map((kpi) => (
          <article key={kpi.id} className="qxa__kpi">
            <p className="qxa__kpi-label">{kpi.label}</p>
            <p className="qxa__kpi-value">{kpi.value}</p>
            {kpi.sub === undefined ? null : <p className="qxa__kpi-sub">{kpi.sub}</p>}
          </article>
        ))}
      </section>

      <div className="qxa__cols">
        <section className="qxa__card" aria-label={la.kpiRevenue}>
          <div className="qxa__card-head">
            <h2 className="qxa__card-title">{la.kpiRevenue}</h2>
          </div>
          <AdminMoneyList rows={view.counts.revenueByCurrency} la={la} />
          <p className="qxa__note">{la.revenuePerCurrency}</p>
        </section>

        <section className="qxa__card" aria-label={la.monthlyTitle}>
          <div className="qxa__card-head">
            <h2 className="qxa__card-title">{la.monthlyTitle}</h2>
          </div>
          <MonthlyBars view={view} la={la} language={language} />
        </section>
      </div>

      <section className="qxa__card" aria-label={la.unownedTitle}>
        <div className="qxa__card-head">
          <h2 className="qxa__card-title">{la.unownedTitle}</h2>
          <span className="qxa__pill qxa__pill--neutral">{String(unowned.length)}</span>
        </div>
        {unowned.length === 0 ? (
          <p className="qxa__empty">{la.none}</p>
        ) : (
          <ul className="qxa__id-list">
            {unowned.map((id) => (
              <li key={id} className="qxa__id-row">
                <Icon name="alert" size={16} />
                <code>{id}</code>
              </li>
            ))}
          </ul>
        )}
        <p className="qxa__note">{la.unownedBody}</p>
      </section>

      <section className="qxa__card" aria-label={la.recentOrdersTitle}>
        <div className="qxa__card-head">
          <h2 className="qxa__card-title">{la.recentOrdersTitle}</h2>
          <div className="qxa__head-tools">
            <button
              type="button"
              className="qxa__btn qxa__btn--ghost"
              onClick={() => setShowAllRecent((v) => !v)}
            >
              {showAllRecent ? la.showFewer : `${la.showAll} (${String(view.recentOrders.length)})`}
            </button>
            <button type="button" className="qxa__btn" onClick={() => onNavigate('/admin/orders')}>
              <Icon name="orders" size={16} />
              <span>{la.seeAllOrders}</span>
            </button>
          </div>
        </div>
        {view.recentOrders.length === 0 ? (
          <p className="qxa__empty">{la.ordersEmpty}</p>
        ) : (
          <div className="qxa__table-wrap">
            <table className="qxa__table">
              <caption className="qxa__sr">{la.recentOrdersTitle}</caption>
              <thead>
                <tr>
                  <th scope="col">{la.colOrder}</th>
                  <th scope="col">{la.colTitle}</th>
                  <th scope="col">{la.colAmount}</th>
                  <th scope="col">{la.colState}</th>
                  <th scope="col">{la.colCreated}</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((order) => (
                  <tr key={order.orderId}>
                    <th scope="row" data-label={la.colOrder}>
                      <code>{order.orderId}</code>
                    </th>
                    <td data-label={la.colTitle}>{order.title}</td>
                    <td data-label={la.colAmount}>
                      {moneyText(order.amountMinorUnits, order.currency, la.noData)}
                    </td>
                    <td data-label={la.colState}>
                      <span className={`qxa__pill qxa__pill--${stateTone(order.state)}`}>
                        {stateLabel(order.state, la)}
                      </span>
                    </td>
                    <td data-label={la.colCreated}>{dateTimeText(order.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
